import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/layout/AppLayout";
import { FlagDetailSlideover } from "../components/FlagDetailSlideover";
import { SeverityPill } from "../components/SeverityPill";
import { formatCurrency } from "../utils/formatCurrency";
import { useProjectWorkbench } from "./useProjectWorkbench";
import type { ProjectSummary } from "../types/dashboard";
import { useOnboarding } from "../context/OnboardingContext";
import { api } from "../lib/apiClient";
import { useAuth } from "../auth/AuthContext";
import { hasApiBaseUrl } from "../lib/orgRuntime";

type ProjectsApiResponse = {
    ok: boolean;
    projects?: ProjectSummary[];
    error?: string;
};

// Heuristic: "Notice" = boundary reinforcement (no request, no action)
// Pure frontend logic – does not affect Vigilo.
function isBoundaryNotice(flag: any): boolean {
    if (!flag) return false;

    const status = (flag.status ?? "").toLowerCase();
    if (status !== "open") return false;

    // Combine title + summary text, normalize formatting
    let text = `${flag.title ?? ""} ${flag.summary ?? ""}`.toLowerCase();

    // Normalize punctuation/em-dashes to spaces
    text = text
        .replace(/—/g, " ") // em dash
        .replace(/–/g, " ") // en dash
        .replace(/-/g, " ") // hyphen
        .replace(/\s+/g, " ") // collapse whitespace
        .trim();

    const boundaryPhrases = [
        "not included",
        "not part of",
        "not part of our baseline",
        "outside the scope",
        "outside of the scope",
        "does not include",
        "doesn't include",
        "does not cover",
        "doesn't cover",
        "excluded from",
        "is excluded",
        "this is excluded",
        "baseline does not include",
        "baseline doesn't include",
        "we are not providing",
        "we're not providing",
    ];

    const requestPhrases = [
        "please",
        "can you",
        "could you",
        "we need",
        "we will need",
        "add ",
        " include",
        "include ",
        "revise",
        "update",
        "provide",
        "send",
        "deliver",
    ];

    const hasBoundary = boundaryPhrases.some((p) => text.includes(p));
    if (!hasBoundary) return false;

    const hasRequest = requestPhrases.some((p) => text.includes(p));
    if (hasRequest) return false;

    return true;
}

/**
 * Build a short, human-readable explanation for evidence snippets
 * so PMs understand why Vigilo latched onto a particular line.
 */
function buildEvidenceExplanation(
    flagTitle: string | null | undefined,
    snippet: string | undefined,
    sourceType?: string,
): string | null {
    if (!flagTitle || !snippet) return null;

    const source = (sourceType ?? "").toLowerCase();

    // Only apply this heuristic for scope-like sources
    const isScopeSource =
        source.includes("scope") ||
        source.includes("baseline") ||
        source.includes("contract");

    if (!isScopeSource) return null;

    const normalizedTitle = flagTitle.toLowerCase();
    const normalizedSnippet = snippet.toLowerCase();

    // Pick "meaningful" words from the title
    const titleWords = normalizedTitle
        .split(/\s+/)
        .map((w) => w.replace(/[^\w]/g, ""))
        .filter((w) => w.length > 3);

    const hasMatch = titleWords.some((w) => normalizedSnippet.includes(w));
    if (!hasMatch) return null;

    return "This snippet aligns closely with the language in the flag title, which is why Vigilo selected it as scope evidence.";
}

// --- Lightweight frontend dedupe for near-duplicate flags ---

const DEDUPE_STOPWORDS = new Set([
    "the",
    "a",
    "an",
    "of",
    "for",
    "and",
    "or",
    "to",
    "in",
    "on",
    "with",
]);

function normalizeTitleForDedupe(title: string | null | undefined): string {
    if (!title) return "";
    return title
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function getTitleTokensForDedupe(title: string | null | undefined): string[] {
    const norm = normalizeTitleForDedupe(title);
    if (!norm) return [];
    return norm.split(" ").filter((t) => t && !DEDUPE_STOPWORDS.has(t));
}

function jaccardSimilarity(a: string[], b: string[]): number {
    if (!a.length && !b.length) return 1;
    if (!a.length || !b.length) return 0;
    const setA = new Set(a);
    const setB = new Set(b);

    let intersection = 0;
    for (const token of setA) {
        if (setB.has(token)) intersection++;
    }

    const unionSize = new Set([...setA, ...setB]).size;
    return unionSize === 0 ? 0 : intersection / unionSize;
}

function dedupeFlags(rawFlags: any[], threshold = 0.9): any[] {
    const result: any[] = [];
    const tokenCache: string[][] = [];
    const normCache: string[] = [];

    for (const flag of rawFlags) {
        const tokens = getTitleTokensForDedupe(flag.title);
        const norm = normalizeTitleForDedupe(flag.title);
        let isDuplicate = false;

        for (let i = 0; i < result.length; i++) {
            const other = result[i];

            // Only dedupe within same classification to be safer
            if (
                other.classification &&
                flag.classification &&
                other.classification !== flag.classification
            ) {
                continue;
            }

            // Fast path: identical normalized titles are always duplicates
            if (norm && norm === normCache[i]) {
                isDuplicate = true;
                break;
            }

            const otherTokens = tokenCache[i];
            const sim = jaccardSimilarity(tokens, otherTokens);

            if (sim >= threshold) {
                isDuplicate = true;
                break;
            }
        }

        if (!isDuplicate) {
            result.push(flag);
            tokenCache.push(tokens);
            normCache.push(norm);
        }
    }

    return result;
}


type TabKey = "open" | "notice" | "dismissed" | "expired" | "all";

export const ProjectWorkbenchPage: React.FC = () => {
    const { orgId } = useAuth();
    const canUseOrgScopedApi = hasApiBaseUrl && !!orgId;
    const [projects, setProjects] = useState<ProjectSummary[]>([]);
    const [projectsError, setProjectsError] = useState<string | null>(null);

    const {
        currentProjectId,
        flags,
        selectedFlagId,
        selectedFlag,
        isSlideoverOpen,
        isLoadingList,
        notesDraft,
        setNotesDraft,
        isSavingNotes,
        isMutatingFlag,
        costAmountDraft,
        setCostAmountDraft,
        costBasisDraft,
        setCostBasisDraft,
        isSavingCost,
        costSuggestion,
        isLoadingSuggestion,
        addServiceDraft,
        isDraftingService,
        addServiceError,
        costSummary,
        flagEvidence,
        isEvidenceLoading,
        evidenceError,
        handleProjectChange,
        openFlagDetails,
        closeSlideover,
        handleDismissFlag,
        handleReinstateFlag,
        handleDraftAdditionalService,
        handleSaveNotes,
        handleSaveCost,
        handleAskAiCostSuggestion,
        handleUseSuggestion,
    } = useProjectWorkbench(projects, {
        onDraftSuccess: () => markStepComplete("export"),
    });

    const { markStepComplete, markSlideoverOpened } = useOnboarding();

    const [activeTab, setActiveTab] = useState<TabKey>("open");
    const [selectedFlagIds, setSelectedFlagIds] = useState<string[]>([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Load projects from API
    const loadProjects = async () => {
        // If API isn't wired yet, fall back to demo project
        if (!canUseOrgScopedApi) {
            setProjects([]);
            return;
        }

        setProjectsError(null);

        try {
            const json: ProjectsApiResponse = await api.get("/projects");

            if (!json.ok) {
                throw new Error(json.error ?? "Failed to load projects");
            }

            const loaded = json.projects ?? [];
            setProjects(loaded);
        } catch (err: any) {
            setProjectsError(err?.message ?? "Failed to load projects");

            // Fallback to mock if nothing else available
            if (projects.length === 0) setProjects([]);
        }
    };

    useEffect(() => {
        void loadProjects();
    }, [canUseOrgScopedApi]);

    // Frontend-only dedupe so PMs don't see the same creep repeated with tiny title variations
    const dedupedFlags = useMemo(
        () => dedupeFlags(flags),
        [flags],
    );

    // Filter flags into categories
    const noticeFlags = useMemo(
        () => dedupedFlags.filter((f) => !f.stale && isBoundaryNotice(f)),
        [dedupedFlags],
    );

    const openFlags = useMemo(
        () =>
            dedupedFlags.filter(
                (f) => !f.stale && f.status === "open" && !isBoundaryNotice(f),
            ),
        [dedupedFlags],
    );

    const dismissedFlags = useMemo(
        () => dedupedFlags.filter((f) => !f.stale && f.status === "dismissed"),
        [dedupedFlags],
    );

    const expiredFlags = useMemo(
        () => dedupedFlags.filter((f) => f.stale === true),
        [dedupedFlags],
    );

    const visibleFlags = useMemo(() => {
        switch (activeTab) {
            case "notice":
                return noticeFlags;
            case "dismissed":
                return dismissedFlags;
            case "expired":
                return expiredFlags;
            case "all":
                return dedupedFlags;
            case "open":
            default:
                return openFlags;
        }
    }, [
        activeTab,
        dedupedFlags,
        openFlags,
        noticeFlags,
        dismissedFlags,
        expiredFlags,
    ]);

    // Debug logging if needed
    console.log("[Workbench] raw flags:", flags.length);
    console.log("[Workbench] deduped:", dedupedFlags.length);
    console.log("[Workbench] visible:", visibleFlags.length);

    const openCount = openFlags.length;
    const noticeCount = noticeFlags.length;
    const dismissedCount = dismissedFlags.length;
    const expiredCount = expiredFlags.length;
    const totalCount = dedupedFlags.length;

    const recoverableAmountLabel =
        costSummary && costSummary.totalAmount > 0
            ? formatCurrency(costSummary.totalAmount)
            : "—";

    const pricedFlagsLabel =
        costSummary && costSummary.totalFlags > 0
            ? `${costSummary.flagsWithCost} / ${costSummary.totalFlags}`
            : "0 / 0";

    // Enhance evidence with frontend-only explanations for ambiguous scope snippets
    const enhancedEvidence = useMemo(() => {
        if (!flagEvidence || !selectedFlag) return flagEvidence;

        return flagEvidence.map((ev: any) => {
            if (!ev || typeof ev.snippet !== "string") {
                return ev;
            }

            const explanation = buildEvidenceExplanation(
                selectedFlag.title ?? "",
                ev.snippet,
                (ev.sourceType ?? ev.documentType ?? ev.source) as
                | string
                | undefined,
            );

            if (!explanation) return ev;

            return {
                ...ev,
                explanation,
            };
        });
    }, [flagEvidence, selectedFlag]);

    // --- Selection helpers ---

    const toggleFlagSelection = (flagId: string) => {
        setSelectedFlagIds((prev) =>
            prev.includes(flagId)
                ? prev.filter((id) => id !== flagId)
                : [...prev, flagId],
        );
    };

    const clearSelection = () => setSelectedFlagIds([]);

    const allVisibleSelected =
        visibleFlags.length > 0 &&
        visibleFlags.every((f: any) => selectedFlagIds.includes(f.id));

    const toggleSelectAllVisible = () => {
        if (allVisibleSelected) {
            clearSelection();
        } else {
            setSelectedFlagIds(visibleFlags.map((f: any) => f.id));
        }
    };

    // When tab changes or visible results change, reset pagination + selection
    useEffect(() => {
        setCurrentPage(1);
        clearSelection();
    }, [activeTab, visibleFlags.length]);

    // Onboarding: mark slideover opened once on first open
    useEffect(() => {
        if (isSlideoverOpen && selectedFlag) {
            markSlideoverOpened();
        }
    }, [isSlideoverOpen, selectedFlag, markSlideoverOpened]);

    // Derived pagination values
    const totalPages =
        visibleFlags.length === 0
            ? 1
            : Math.ceil(visibleFlags.length / pageSize);

    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pagedFlags = visibleFlags.slice(startIndex, endIndex);

    const handleProjectChangeWithReset = (projectId: string) => {
        handleProjectChange(projectId);
        setActiveTab("open");
        setCurrentPage(1);
        clearSelection();
    };

    return (
        <AppLayout
            projects={projects}
            currentProjectId={currentProjectId}
            onProjectChange={handleProjectChangeWithReset}
        >
            <div className="flex h-full flex-col gap-4 px-5 py-4 lg:px-7 lg:py-5">
                {/* Header */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-1.5">
                        <h1 className="text-xl font-semibold text-slate-50">
                            Project workbench
                        </h1>
                        <p className="text-sm text-slate-300">
                            Triage scope creep, attach evidence, and price
                            additional services in one place.
                        </p>
                    </div>
                    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                                Project
                            </span>
                            <select
                                value={currentProjectId ?? ""}
                                onChange={(e) =>
                                    handleProjectChangeWithReset(
                                        e.target.value,
                                    )
                                }
                                className="w-56 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-50 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-0"
                            >
                                {projects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* KPI row */}
                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                                    Flags
                                </div>
                                <div className="mt-1 text-2xl font-semibold text-slate-50">
                                    {openCount}{" "}
                                    <span className="text-sm font-normal text-slate-400">
                                        open
                                    </span>
                                </div>
                            </div>
                            <div className="text-xs text-slate-400">
                                {totalCount} total
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                                    Priced flags
                                </div>
                                <div className="mt-1 text-2xl font-semibold text-slate-50">
                                    {pricedFlagsLabel}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                                    Estimated recoverable fees
                                </div>
                                <div className="mt-1 text-2xl font-semibold text-amber-400">
                                    {recoverableAmountLabel}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
                    {/* Flags table */}
                    <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-slate-800 bg-slate-950/80 overflow-hidden">
                        {/* Tabs + selection summary */}
                        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 flex-shrink-0">
                            <div className="flex gap-2">
                                {/* Open */}
                                <button
                                    type="button"
                                    onClick={() => setActiveTab("open")}
                                    className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium ${activeTab === "open"
                                        ? "bg-slate-100 text-slate-900"
                                        : "bg-slate-900 text-slate-200 hover:bg-slate-800"
                                        }`}
                                >
                                    Open
                                    <span className="ml-1 rounded-full bg-slate-800 px-1.5 py-0.5 text-[11px]">
                                        {openCount}
                                    </span>
                                </button>

                                {/* Notice (boundary mentions, no action) */}
                                <button
                                    type="button"
                                    onClick={() => setActiveTab("notice")}
                                    className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium ${activeTab === "notice"
                                        ? "bg-slate-100 text-slate-900"
                                        : "bg-slate-900 text-slate-200 hover:bg-slate-800"
                                        }`}
                                >
                                    Notice
                                    <span className="ml-1 rounded-full bg-slate-800 px-1.5 py-0.5 text-[11px]">
                                        {noticeCount}
                                    </span>
                                </button>

                                {/* Dismissed */}
                                <button
                                    type="button"
                                    onClick={() => setActiveTab("dismissed")}
                                    className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium ${activeTab === "dismissed"
                                        ? "bg-slate-100 text-slate-900"
                                        : "bg-slate-900 text-slate-200 hover:bg-slate-800"
                                        }`}
                                >
                                    Dismissed
                                    <span className="ml-1 rounded-full bg-slate-800 px-1.5 py-0.5 text-[11px]">
                                        {dismissedCount}
                                    </span>
                                </button>

                                {/* Expired */}
                                <button
                                    type="button"
                                    onClick={() => setActiveTab("expired")}
                                    className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium ${activeTab === "expired"
                                        ? "bg-slate-100 text-slate-900"
                                        : "bg-slate-900 text-slate-200 hover:bg-slate-800"
                                        }`}
                                >
                                    Expired
                                    <span className="ml-1 rounded-full bg-slate-800 px-1.5 py-0.5 text-[11px]">
                                        {expiredCount}
                                    </span>
                                </button>

                                {/* All */}
                                <button
                                    type="button"
                                    onClick={() => setActiveTab("all")}
                                    className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium ${activeTab === "all"
                                        ? "bg-slate-100 text-slate-900"
                                        : "bg-slate-900 text-slate-200 hover:bg-slate-800"
                                        }`}
                                >
                                    All
                                    <span className="ml-1 rounded-full bg-slate-800 px-1.5 py-0.5 text-[11px]">
                                        {totalCount}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Table with scrollable body */}
                        <div className="min-h-0 flex-1 flex flex-col overflow-hidden">
                            <div className="flex-1 overflow-auto">
                                <table className="min-w-full text-left text-sm">
                                    <thead className="sticky top-0 bg-slate-950/95 text-xs uppercase tracking-wide text-slate-400 backdrop-blur">
                                        <tr>
                                            <th className="w-10 px-4 py-3 font-medium">
                                                <input
                                                    type="checkbox"
                                                    aria-label="Select all visible flags"
                                                    checked={allVisibleSelected}
                                                    onChange={toggleSelectAllVisible}
                                                    className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-amber-500 focus:ring-0"
                                                />
                                            </th>
                                            <th className="px-4 py-3 font-medium">
                                                Flag
                                            </th>
                                            <th className="px-4 py-3 font-medium">
                                                Severity
                                            </th>
                                            <th className="px-4 py-3 font-medium">
                                                Classification
                                            </th>
                                            <th className="px-4 py-3 font-medium text-right">
                                                Est. cost
                                            </th>
                                            <th className="px-4 py-3 font-medium">
                                                Source
                                            </th>
                                            <th className="px-4 py-3 font-medium">
                                                Created
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {isLoadingList && (
                                            <tr>
                                                <td
                                                    colSpan={7}
                                                    className="px-4 py-8 text-center text-sm text-slate-400"
                                                >
                                                    Loading flags…
                                                </td>
                                            </tr>
                                        )}

                                        {!isLoadingList && pagedFlags.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan={7}
                                                    className="px-4 py-8 text-center text-sm text-slate-400"
                                                >
                                                    No flags in this view yet.
                                                </td>
                                            </tr>
                                        )}

                                        {!isLoadingList &&
                                            pagedFlags.map((flag) => {
                                                const isRowSelected =
                                                    flag.id === selectedFlagId;
                                                const isChecked =
                                                    selectedFlagIds.includes(flag.id);

                                                const createdLabel = flag.createdAt
                                                    ? new Date(
                                                        flag.createdAt,
                                                    ).toLocaleDateString()
                                                    : "";

                                                const sourceLabel =
                                                    flag.documentTitle ??
                                                    flag.documentType ??
                                                    flag.source;

                                                return (
                                                    <tr
                                                        key={flag.id}
                                                        className={`cursor-pointer border-t border-slate-800/80 ${isRowSelected
                                                            ? "bg-slate-900/80"
                                                            : "hover:bg-slate-900/50"
                                                            }`}
                                                        onClick={() => {
                                                            openFlagDetails(flag.id);
                                                            markStepComplete("reviewFlag");
                                                            markSlideoverOpened();
                                                        }}
                                                    >
                                                        <td className="px-4 py-3 align-middle">
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleFlagSelection(
                                                                        flag.id,
                                                                    );
                                                                }}
                                                                className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-amber-500 focus:ring-0"
                                                            />
                                                        </td>
                                                        <td className="max-w-[320px] px-4 py-3 align-top">
                                                            <div className="flex flex-col gap-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="line-clamp-1 text-sm font-medium text-slate-50">
                                                                        {flag.title ??
                                                                            "Untitled flag"}
                                                                    </span>
                                                                    {flag.status === "dismissed" && (
                                                                        <span className="rounded-full bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-300">
                                                                            Dismissed
                                                                        </span>
                                                                    )}
                                                                    {flag.evidenceCount > 0 && (
                                                                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-900/40 px-2.5 py-0.5 text-[11px] font-medium text-emerald-300">
                                                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                                                            Receipts
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {flag.summary && (
                                                                    <p className="line-clamp-2 text-xs text-slate-400">
                                                                        {flag.summary}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 align-top">
                                                            <SeverityPill
                                                                severity={flag.severity}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 align-top">
                                                            <span className="inline-flex rounded-md bg-slate-900 px-2.5 py-0.5 text-xs text-slate-200">
                                                                {flag.classification ??
                                                                    "Unclassified"}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 align-top text-right text-sm text-slate-200">
                                                            {flag.costEstimate
                                                                ? formatCurrency(
                                                                    flag.costEstimate.amount,
                                                                )
                                                                : "—"}
                                                        </td>
                                                        <td className="px-4 py-3 align-top text-xs text-slate-400">
                                                            {sourceLabel}
                                                        </td>
                                                        <td className="whitespace-nowrap px-4 py-3 align-top text-xs text-slate-400">
                                                            {createdLabel}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination footer */}
                            <div className="flex-shrink-0 border-t border-slate-800 px-4 py-3 text-xs text-slate-400">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        Showing{" "}
                                        {visibleFlags.length === 0 ? 0 : startIndex + 1} –{" "}
                                        {Math.min(endIndex, visibleFlags.length)} of{" "}
                                        {visibleFlags.length} flags
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            disabled={safePage <= 1}
                                            onClick={() =>
                                                setCurrentPage((p) => Math.max(1, p - 1))
                                            }
                                            className="inline-flex items-center rounded-md border border-slate-700 px-2.5 py-1 text-xs font-medium text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Prev
                                        </button>
                                        <span>
                                            Page {safePage} of {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={safePage >= totalPages}
                                            onClick={() =>
                                                setCurrentPage((p) =>
                                                    Math.min(totalPages, p + 1),
                                                )
                                            }
                                            className="inline-flex items-center rounded-md border border-slate-700 px-2.5 py-1 text-xs font-medium text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Slideover */}
                {isSlideoverOpen && selectedFlag && (
                    <FlagDetailSlideover
                        flag={selectedFlag}
                        notesDraft={notesDraft}
                        onNotesChange={setNotesDraft}
                        onSaveNotes={handleSaveNotes}
                        isSavingNotes={isSavingNotes}
                        onClose={closeSlideover}
                        canDismiss={selectedFlag.status === "open"}
                        isMutating={isMutatingFlag}
                        onDismiss={() =>
                            handleDismissFlag(selectedFlag.id)
                        }
                        onReinstate={() =>
                            handleReinstateFlag(selectedFlag.id)
                        }
                        costAmountDraft={costAmountDraft}
                        onCostAmountChange={setCostAmountDraft}
                        costBasisDraft={costBasisDraft}
                        onCostBasisChange={setCostBasisDraft}
                        onSaveCost={handleSaveCost}
                        isSavingCost={isSavingCost}
                        costSuggestion={costSuggestion}
                        onAskSuggestion={handleAskAiCostSuggestion}
                        isLoadingSuggestion={isLoadingSuggestion}
                        onUseSuggestion={handleUseSuggestion}
                        addServiceDraft={addServiceDraft}
                        isDraftingService={isDraftingService}
                        addServiceError={addServiceError}
                        onDraftAdditionalService={() =>
                            handleDraftAdditionalService(selectedFlag.id, selectedFlagIds)
                        }
                        evidence={enhancedEvidence}
                        isEvidenceLoading={isEvidenceLoading}
                        evidenceError={evidenceError}
                        selectedFlagIds={selectedFlagIds}
                    />
                )}

            </div>
        </AppLayout>
    );
};
